import type {
  CabinetCreateAppointmentRequest,
  CabinetWarningCode,
  ScheduleResult,
} from "@planerius/contracts/cabinet"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { fetchClients } from "../../api/clients.api"
import type { BookingCatalog } from "../../api/journal-actions.api"
import {
  createAppointment,
  createSlotHold,
  fetchBookingCatalog,
  releaseSlotHold,
} from "../../api/journal-actions.api"
import { fetchJournal } from "../../api/journal.api"
import { isApiProblem, transportProblem } from "../../api/problem"
import type {
  BookingEvent,
  BookingState,
  FreeWindow,
  OpenSlot,
} from "../../journal/booking-draft"
import {
  bookingReducer,
  canLookup,
  canSubmit,
  CONSENT_PERSONAL_DATA_VERSION,
  draftIntent,
  openDraft,
  slotKey,
} from "../../journal/booking-draft"
import { freeWindows } from "../../journal/free-slots"
import { createIdempotencyBook } from "../../journal/idempotency-book"
import { formatMinute, instantFromLocal, minutesOfDay } from "../../journal/journal-time"
import { useSecondsNow } from "../../journal/use-journal-clock"
import { formatDateLong, shiftDate, todayInZone } from "../../lib/format"
import { failureText } from "./use-journal-actions"

/**
 * Сеанс быстрой формы: черновик, поиск клиента, удержание слота, создание записи
 * и подбор окон после отказа «слот заняли».
 *
 * ГЛАВНОЕ РЕШЕНИЕ — ЧЕРНОВИК ЖИВЁТ ЗДЕСЬ, А НЕ В ФОРМЕ. Форма рисует и только рисует;
 * состояние переживает и её перерисовку, и сворачивание по Escape, и смену дня журнала
 * при переходе на «завтра, 11:00». Сеанс начинается заново ТОЛЬКО тогда, когда человек
 * открыл ДРУГОЙ слот — и это правило выражено одной строкой (openedFor), а не набором
 * эвристик: пока ключ слота тот же, введённое остаётся.
 *
 * ТОНКОЕ МЕСТО. Выбор нового окна меняет и черновик, и адрес. Адрес приезжает следующим
 * рендером, и если бы сеанс сверялся с ним напрямую, он успел бы посчитать себя
 * «открытым на другом слоте» и стереть всё введённое — ровно то, чего этот экран
 * не имеет права делать. Поэтому openedFor двигается ВМЕСТЕ с черновиком, в одном
 * обновлении состояния, и адрес его только догоняет.
 */

/**
 * Сколько свободных окон показывать.
 *
 * Двенадцать — это примерно полтора экрана телефона: больше человек не читает,
 * а меньше выглядит как «свободного времени почти нет». Когда окон больше, экран
 * говорит это словами: список, молча обрезанный, выдаёт начало смены за весь день.
 */
const WINDOW_LIMIT = 12

interface Session {
  /** Ключ слота, на котором сеанс открыт. Сверяется с адресом. */
  readonly openedFor: string
  readonly state: BookingState
}

/**
 * Свободные окна выбранного дня для шага «Время» (5-А2).
 *
 * ОТДЕЛЬНО ОТ ПОДБОРА ПОСЛЕ ОТКАЗА. Тот подбор — реакция на «это время заняли»:
 * он ищет ближайшее свободное и предлагает переехать. Здесь же человек ВЫБИРАЕТ
 * время с нуля, и ему нужен список окон запрошенного дня, а не одно ближайшее.
 */
export interface BookingDayWindows {
  readonly date: string
  readonly status: "loading" | "ready" | "failed"
  readonly windows: readonly FreeWindow[]
  /**
   * Для кого и подо что посчитаны.
   *
   * БЕЗ ЭТОГО ОКНА ПЕРЕЖИВАЮТ СВОЙ СМЫСЛ. Человек посмотрел окна Анны, вернулся
   * на шаг «мастер», выбрал Дмитрия — и старые кнопки остались на экране. Нажатие
   * на них молча перенесло бы запись обратно к Анне: в самом окне лежит её
   * employeeId. То же с составом услуг: окна, посчитанные под сорок пять минут,
   * для визита на час — это наложение на следующего клиента.
   */
  readonly employeeId: string
  readonly durationMinutes: number
  /** Показаны не все: список обрезан, и человеку это сказано. */
  readonly truncated: boolean
}

export interface BookingSession {
  readonly state: BookingState
  readonly dispatch: (event: BookingEvent) => void
  readonly catalog: BookingCatalog | null
  readonly catalogFailed: boolean
  /** Секунд до конца удержания. null — удержания нет. */
  readonly holdSecondsLeft: number | null
  readonly submit: (notifyClient: boolean) => void
  readonly pickWindow: (window: FreeWindow) => void
  /** Окна выбранного дня на шаге «Время». null — день ещё не спрашивали. */
  readonly dayWindows: BookingDayWindows | null
  /** Показать свободные окна этого дня. Черновик НЕ меняется, пока окно не выбрано. */
  readonly pickDay: (date: string) => void
  readonly retryWindows: (all: boolean) => void
  /**
   * «Придержать время» (2-2.4.1): закрыть форму, НЕ отпуская слот. Клиент перезвонит
   * через десять минут, а время до его звонка остаётся за салоном.
   *
   * null — удерживать нечего: права holds.manage нет, удержание не встало или уже
   * истекло. Кнопка, которая обещает придержать, но ничего не держит, хуже её отсутствия.
   */
  readonly keepHold: (() => void) | null
}

export interface BookingSessionInput {
  /** Форма открыта (в адресе есть слот). Свёрнутая форма сеанс не убивает. */
  readonly open: boolean
  readonly slot: OpenSlot | null
  readonly branchId: string
  readonly timeZone: string
  /** День журнала: из него считаются свободные окна без запроса к серверу. */
  readonly result: ScheduleResult | null
  /** Сейчас в минутах от начала дня — чтобы не предлагать прошедшее время. */
  readonly nowMinute: number
  readonly stepMinutes: number
  readonly canEdit: boolean
  /** Право holds.manage. Без него форма работает без удержания и без отсчёта. */
  readonly canHold: boolean
  readonly employeeName: (employeeId: string | null) => string
  /** Запись создана: перечитать день и закрыть форму. */
  readonly onCreated: () => void
  /** Слот придержан без записи: закрыть форму и перечитать день — удержание в нём. */
  readonly onHeld: () => void
  /** Слот черновика изменился — обновить адрес (и день, если окно завтрашнее). */
  readonly onSlotChanged: (slot: OpenSlot) => void
}

/** Сколько дней вперёд искать ближайшее окно, когда на сегодня у мастера пусто. */
const NEXT_DAYS_LOOKAHEAD = 7

export function useBookingSession(input: BookingSessionInput): BookingSession | null {
  const [session, setSession] = useState<Session | null>(null)
  // Ссылка на текущий сеанс для обработчиков: они не должны пересоздаваться на каждую
  // набранную букву, иначе таймеры и запросы перезапускаются вместе с ними.
  const sessionRef = useRef<Session | null>(null)
  sessionRef.current = session
  const [catalog, setCatalog] = useState<BookingCatalog | null>(null)
  const [catalogFailed, setCatalogFailed] = useState(false)
  const bookRef = useRef(createIdempotencyBook())

  /*
   * «Придержать время» вместо «записать». Признак живёт в ref, а не в состоянии:
   * его читает ФУНКЦИЯ ОЧИСТКИ эффекта удержания, а она видит значения того рендера,
   * в котором эффект был поставлен, — состояние там было бы вчерашним.
   */
  const keepRef = useRef(false)
  /*
   * ПРАВО НА ПРАВКУ ЧИТАЕТСЯ ОДИН РАЗ, ПРИ ПОСТАНОВКЕ УДЕРЖАНИЯ.
   *
   * Раньше canEdit стояло в зависимостях эффекта, и один неудачный тик фонового опроса
   * (таймаут, 5xx) переводил экран в режим «нет связи» — эффект перезапускался, очистка
   * отпускала слот, а отсчёт над ним продолжал идти. Администратор в этот момент диктует
   * это время клиенту по телефону, и слот уже ничей. Связь — состояние минуты, а удержание
   * живёт ради разговора; связывать их нельзя.
   */
  const canEditRef = useRef(input.canEdit)
  canEditRef.current = input.canEdit
  const onHeldRef = useRef(input.onHeld)
  onHeldRef.current = input.onHeld
  const onHeld = useCallback((): void => {
    onHeldRef.current()
  }, [])

  const key = input.slot === null ? null : slotKey(input.slot.date, input.slot.startMinute, input.slot.employeeId)
  const slotRef = useRef(input.slot)
  slotRef.current = input.slot

  // Открытие слота. Тот же ключ — тот же сеанс: свернули по Escape, кликнули туда же,
  // вернулись к своему вводу.
  useEffect(() => {
    if (key === null) return
    setSession((current) => {
      if (current !== null && current.openedFor === key) return current
      const slot = slotRef.current
      if (slot === null) return current
      return { openedFor: key, state: openDraft(slot) }
    })
  }, [key])

  const dispatch = useCallback((event: BookingEvent): void => {
    setSession((current) =>
      current === null ? current : { ...current, state: bookingReducer(current.state, event) },
    )
  }, [])

  // ─────────── каталог услуг ───────────
  useEffect(() => {
    if (!input.open || catalog !== null || catalogFailed) return
    const controller = new AbortController()
    void (async () => {
      try {
        const loaded = await fetchBookingCatalog(controller.signal)
        if (!controller.signal.aborted) setCatalog(loaded)
      } catch {
        // Каталог не обязателен для чтения журнала, поэтому его отказ не роняет экран:
        // форма скажет, что записать сейчас нельзя, и объяснит почему.
        if (!controller.signal.aborted) setCatalogFailed(true)
      }
    })()
    return () => {
      controller.abort()
    }
  }, [input.open, catalog, catalogFailed])

  // ─────────── поиск клиента по телефону ───────────
  const phone = session?.state.draft.phone ?? ""
  useEffect(() => {
    if (!input.open || !canLookup(phone)) return
    const controller = new AbortController()
    // Пауза перед запросом: администратор набирает номер с трубкой у уха, и запрос
    // на каждую цифру — это одиннадцать запросов подряд и мигающий результат.
    const timer = setTimeout(() => {
      dispatch({ type: "lookup-started" })
      void (async () => {
        try {
          const found = await fetchClients({ search: phone, limit: 5 }, controller.signal)
          if (controller.signal.aborted) return
          dispatch({ type: "lookup-result", clients: found.items })
        } catch (error) {
          if (controller.signal.aborted) return
          const problem = isApiProblem(error) ? error : transportProblem("unknown")
          dispatch({
            type: "lookup-failed",
            message:
              problem.code === "permission_denied"
                ? "Поиск по базе клиентов недоступен вашей роли."
                : `${problem.title}: поиск не выполнен.`,
          })
        }
      })()
    }, 350)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [phone, input.open, dispatch])

  // ─────────── удержание слота ───────────
  const openedFor = session?.openedFor ?? null
  const employeeId = session?.state.draft.employeeId ?? null
  const draftDate = session?.state.draft.date ?? null
  const draftMinute = session?.state.draft.startMinute ?? 0

  useEffect(() => {
    if (!input.open || !input.canHold) return
    if (!canEditRef.current) return
    if (employeeId === null || draftDate === null || openedFor === null) return

    // УЖЕ ДЕРЖИМ ЭТОТ СЛОТ — ВТОРОЕ УДЕРЖАНИЕ НЕ СТАВИМ. Так бывает после «Придержать
    // время»: форма закрылась, холд остался жить, клиент перезвонил, администратор
    // кликает ту же ячейку. Запрос нового удержания упёрся бы в своё же собственное
    // (сервер честно считает его чужим конфликтом), отсчёт исчезал бы, а кнопка
    // «Придержать время» пропадала — при живом, оплаченном разговором удержании.
    const known = sessionRef.current
    if (
      known !== null &&
      known.openedFor === openedFor &&
      known.state.hold !== null &&
      Date.parse(known.state.hold.expiresAt) > Date.now()
    ) {
      return
    }

    const controller = new AbortController()
    let placed: string | null = null
    keepRef.current = false

    void (async () => {
      try {
        // Удерживается ЯЧЕЙКА, в которую кликнули, а не вся будущая услуга: пока
        // услуга не выбрана, длительность визита неизвестна, а запирать час чужого
        // времени «на всякий случай» нельзя — это время других клиентов.
        const startAt = instantFromLocal(draftDate, draftMinute, input.timeZone)
        const endAt = instantFromLocal(
          draftDate,
          draftMinute + Math.max(input.stepMinutes, 5),
          input.timeZone,
        )
        const hold = await createSlotHold(
          { branchId: input.branchId, employeeId, startAt, endAt },
          bookRef.current.keyFor(`hold:${openedFor}`),
          controller.signal,
        )
        if (controller.signal.aborted) {
          void releaseSlotHold(hold.id)
          return
        }
        placed = hold.id
        dispatch({ type: "hold-set", hold: { id: hold.id, expiresAt: hold.expiresAt } })
      } catch {
        // Удержание — удобство, а не условие записи: чужое удержание, отсутствие права
        // или обрыв связи не должны мешать оформлять визит.
        if (!controller.signal.aborted) dispatch({ type: "hold-set", hold: null })
      }
    })()

    return () => {
      controller.abort()
      bookRef.current.forget(`hold:${openedFor}`)
      // Своё удержание снимается при уходе с формы: иначе слот простоит запертым
      // до конца TTL, хотя администратор уже передумал.
      //
      // Кроме случая, когда придержать время И БЫЛО РЕШЕНИЕМ: «клиент перезвонит через
      // десять минут» — тогда слот остаётся за салоном до конца TTL, и в сетке он виден
      // как своё удержание с подписью «держите вы · снять».
      if (placed !== null && !keepRef.current) {
        void releaseSlotHold(placed)
        // Отсчёт над отпущенным слотом — обещание, которого больше нет.
        dispatch({ type: "hold-set", hold: null })
      }
    }
    // Удержание ставится на слот и переезжает вместе с ним (смена мастера в форме —
    // это другой слот), но НЕ пересоздаётся на каждую набранную в форме букву.
  }, [
    openedFor,
    employeeId,
    draftDate,
    draftMinute,
    input.open,
    input.canHold,
    input.branchId,
    dispatch,
    input.timeZone,
    input.stepMinutes,
  ])

  const holdExpiresAt = session?.state.hold?.expiresAt ?? null
  const holdId = session?.state.hold?.id ?? null
  const secondsNow = useSecondsNow(holdExpiresAt !== null && input.open)
  const holdSecondsLeft =
    holdExpiresAt === null ? null : Math.max(0, Math.round((Date.parse(holdExpiresAt) - secondsNow) / 1000))

  // ─────────── подбор свободных окон после отказа ───────────
  const { result, nowMinute, stepMinutes, branchId, timeZone } = input
  const searchWindows = useCallback(
    (all: boolean): void => {
      const current = slotRef.current
      const state = sessionRef.current?.state
      if (state === undefined || current === null) return
      const master = state.draft.employeeId
      dispatch({ type: "windows-searching" })

      if (master === null) {
        dispatch({ type: "windows-none", next: null })
        return
      }

      const duration = state.draft.durationMinutes
      const date = state.draft.date

      void (async () => {
        try {
          // Сегодня окна считаются из УЖЕ загруженного дня: второй запрос за теми же
          // данными сделал бы ожидание там, где ответ уже на экране.
          const today =
            result === null
              ? []
              : freeWindows({
                  result,
                  date,
                  employeeId: master,
                  durationMinutes: duration,
                  stepMinutes,
                  notBeforeMinute: nowMinute,
                  limit: all ? 12 : 3,
                })

          if (today.length > 0) {
            dispatch({
              type: "windows-found",
              windows: today.map((minute) => ({
                date,
                minute,
                employeeId: master,
                label: formatMinute(minute),
              })),
            })
            return
          }

          // На сегодня пусто — ищем ближайший день ОТДЕЛЬНЫМ запросом. Это и есть
          // поведение макета: код отказа приходит без окон, окна запрашиваются после.
          for (let ahead = 1; ahead <= NEXT_DAYS_LOOKAHEAD; ahead += 1) {
            const nextDate = shiftDate(date, ahead)
            const day = await fetchJournal({
              branchId,
              dateFrom: nextDate,
              dateTo: nextDate,
              employeeIds: [master],
            })
            const windows = freeWindows({
              result: day,
              date: nextDate,
              employeeId: master,
              durationMinutes: duration,
              stepMinutes,
              limit: 1,
            })
            const first = windows[0]
            if (first !== undefined) {
              dispatch({
                type: "windows-none",
                next: {
                  date: nextDate,
                  minute: first,
                  employeeId: master,
                  label:
                    ahead === 1
                      ? `Завтра ${formatMinute(first)}`
                      : `${formatDateLong(nextDate, timeZone)}, ${formatMinute(first)}`,
                },
              })
              return
            }
          }

          dispatch({ type: "windows-none", next: null })
        } catch {
          dispatch({ type: "windows-failed" })
        }
      })()
    },
    [dispatch, result, nowMinute, stepMinutes, branchId, timeZone],
  )

  /*
   * ОКНА ВЫБРАННОГО ДНЯ.
   *
   * День выбирается чипом («Сегодня», «Завтра», дата), но ЧЕРНОВИК ПРИ ЭТОМ НЕ
   * МЕНЯЕТСЯ: человек ещё только смотрит, что свободно. Черновик двигает выбор
   * конкретного окна — pickWindow, — и вместе с ним едут удержание и адрес.
   * Иначе каждое нажатие на «Завтра» отпускало бы удержание сегодняшнего времени,
   * о котором человек ещё не решил.
   */
  const [dayWindows, setDayWindows] = useState<BookingDayWindows | null>(null)
  const dayRef = useRef(0)

  const pickDay = useCallback(
    (date: string): void => {
      const state = sessionRef.current?.state
      if (state === undefined) return
      const master = state.draft.employeeId
      const duration = state.draft.durationMinutes
      const token = dayRef.current + 1
      dayRef.current = token
      setDayWindows({
        date,
        status: "loading",
        windows: [],
        employeeId: master ?? "",
        durationMinutes: duration,
        truncated: false,
      })

      if (master === null) {
        // Без мастера окна считать не от чего: свободное время — это чьё-то
        // свободное время. Экран скажет это словами и оставит ручной ввод.
        setDayWindows({
          date,
          status: "ready",
          windows: [],
          employeeId: "",
          durationMinutes: duration,
          truncated: false,
        })
        return
      }

      void (async () => {
        try {
          // Сегодняшний день уже загружен журналом — второй запрос за теми же
          // данными сделал бы ожидание там, где ответ уже на экране.
          // Журнал загружен на ОДИН день (dateFrom = dateTo), и если спрашивают его —
          // второй запрос за теми же данными сделал бы ожидание там, где ответ уже
          // на экране.
          const loaded = result?.days[0]?.date ?? null
          const day =
            result !== null && date === loaded
              ? result
              : await fetchJournal({
                  branchId,
                  dateFrom: date,
                  dateTo: date,
                  employeeIds: [master],
                })
          const minutes = freeWindows({
            result: day,
            date,
            employeeId: master,
            durationMinutes: duration,
            stepMinutes,
            /*
             * Прошедшее время не свободно — но только СЕГОДНЯ, и минута считается
             * от сегодняшнего дня, а не от дня журнала. nowMinute приходит как
             * «минут от начала показанного дня»: открыв журнал на завтра и спросив
             * окна на сегодня, им отсекли бы не то время.
             */
            ...(date === todayInZone(timeZone) ? { notBeforeMinute: minutesOfDay(new Date(), timeZone) } : {}),
            limit: WINDOW_LIMIT,
          })
          if (dayRef.current !== token) return
          setDayWindows({
            date,
            status: "ready",
            windows: minutes.map((minute) => ({
              date,
              minute,
              employeeId: master,
              label: formatMinute(minute),
            })),
            employeeId: master,
            durationMinutes: duration,
            truncated: minutes.length >= WINDOW_LIMIT,
          })
        } catch {
          if (dayRef.current !== token) return
          setDayWindows({
            date,
            status: "failed",
            windows: [],
            employeeId: master,
            durationMinutes: duration,
            truncated: false,
          })
        }
      })()
    },
    [result, branchId, stepMinutes, timeZone],
  )

  /**
   * Выбран другой день визита.
   *
   * ЧЕРНОВИК ДВИГАЕТСЯ ВМЕСТЕ С ПОКАЗОМ. Сначала чип менял только список окон —
   * и запись, время которой поставили вручную, уходила на день журнала: на экране
   * «Завтра», в базе сегодня. Удержание при этом снимается редьюсером: оно стояло
   * на другом дне.
   */
  const pickDayForDraft = useCallback(
    (date: string): void => {
      const current = sessionRef.current
      if (current === null) return
      if (current.state.draft.date === date) {
        pickDay(date)
        return
      }
      const nextState = bookingReducer(current.state, { type: "day", date })
      setSession({
        openedFor: slotKey(date, nextState.draft.startMinute, nextState.draft.employeeId),
        state: nextState,
      })
      input.onSlotChanged({
        date,
        startMinute: nextState.draft.startMinute,
        employeeId: nextState.draft.employeeId,
        durationMinutes: nextState.draft.durationMinutes,
      })
      pickDay(date)
    },
    [input, pickDay],
  )

  const pickWindow = useCallback(
    (window: FreeWindow): void => {
      const current = sessionRef.current
      if (current === null) return
      const nextState = bookingReducer(current.state, { type: "pick-window", window })
      setSession({
        openedFor: slotKey(window.date, window.minute, window.employeeId),
        state: nextState,
      })
      input.onSlotChanged({
        date: window.date,
        startMinute: window.minute,
        employeeId: window.employeeId,
        durationMinutes: nextState.draft.durationMinutes,
      })
    },
    [input],
  )

  const submit = useCallback(
    (notifyClient: boolean): void => {
      const current = sessionRef.current
      if (current === null) return
      const state = current.state
      if (!canSubmit(state) || !input.canEdit) return
      const draft = state.draft
      if (draft.serviceIds.length === 0) return

      const intent = draftIntent(draft)
      const idempotencyKey = bookRef.current.keyFor(intent)
      const acknowledged = state.warnings.map((warning) => warning.code as CabinetWarningCode)

      const request: CabinetCreateAppointmentRequest = {
        branchId: input.branchId,
        startAt: instantFromLocal(draft.date, draft.startMinute, input.timeZone),
        // Порядок сохраняется: сервер считает шаги подряд в том порядке,
        // в каком их прислали, и «стрижка, потом борода» — не то же самое,
        // что наоборот.
        items: draft.serviceIds.map((serviceId) => ({ serviceId })),
        client:
          draft.clientId !== null
            ? { clientId: draft.clientId }
            : {
                displayName: draft.clientName.trim(),
                phone: draft.phone.trim(),
                consents: {
                  // personalData обязан быть true: карточку без согласия завести нельзя.
                  personalData: true,
                  personalDataVersion: CONSENT_PERSONAL_DATA_VERSION,
                  ads: draft.consentAds,
                  ...(draft.consentAds
                    ? { adsVersion: CONSENT_PERSONAL_DATA_VERSION }
                    : {}),
                  // «Получено устно» — так это и происходит у стойки с трубкой в руке.
                  capturedVia: "verbal",
                },
              },
        acknowledgedWarnings: acknowledged,
        notifyClient,
        ...(draft.employeeId === null ? {} : { employeeId: draft.employeeId }),
        ...(draft.comment.trim() === "" ? {} : { comment: draft.comment.trim() }),
        ...(state.hold === null ? {} : { holdId: state.hold.id }),
      }

      dispatch({ type: "submit-started" })

      void (async () => {
        try {
          await createAppointment(request, idempotencyKey)
          bookRef.current.forget(intent)
          // Сеанс закончен: запись создана, и повторный клик по тому же месту обязан
          // открыть ЧИСТУЮ форму, а не черновик уже оформленного визита.
          setSession(null)
          input.onCreated()
        } catch (error) {
          const problem = isApiProblem(error) ? error : transportProblem("unknown")
          // Повторять тем же ключом имеет смысл только обрыв связи: остальное —
          // новое намерение, и второй ключ обязателен, иначе сервер вернёт
          // сохранённый ответ прошлой попытки, то есть тот же самый отказ.
          if (!problem.retryable) bookRef.current.forget(intent)

          if (
            problem.code === "busy_conflicts" ||
            problem.code === "slot_taken" ||
            problem.code === "hold_expired"
          ) {
            dispatch({
              type: "slot-taken",
              takenLabel: formatMinute(draft.startMinute),
              employeeName: input.employeeName(draft.employeeId),
            })
            searchWindows(false)
            return
          }

          if (problem.code === "confirmation_required" && problem.warnings.length > 0) {
            dispatch({
              type: "warnings",
              warnings: problem.warnings.map((warning) => ({
                code: warning.code,
                message: warning.message,
              })),
            })
            return
          }

          dispatch({ type: "submit-failed", message: failureText(problem) })
        }
      })()
    },
    [dispatch, input, searchWindows],
  )

  return useMemo<BookingSession | null>(() => {
    if (!input.open || session === null) return null
    return {
      state: session.state,
      dispatch,
      catalog,
      catalogFailed,
      holdSecondsLeft,
      submit,
      pickWindow,
      dayWindows,
      pickDay: pickDayForDraft,
      retryWindows: searchWindows,
      keepHold:
        holdId === null || holdSecondsLeft === null || holdSecondsLeft <= 0
          ? null
          : (): void => {
              keepRef.current = true
              onHeld()
            },
    }
  }, [
    input.open,
    session,
    dispatch,
    catalog,
    catalogFailed,
    holdSecondsLeft,
    holdId,
    onHeld,
    submit,
    pickWindow,
    dayWindows,
    pickDayForDraft,
    searchWindows,
  ])
}
